/**
 * Maatwerk-configurator (snippets/maatwerk-configurator.liquid)
 *
 * - Variantrijen sturen de bestaande <variant-picker> aan (radio + change-event). Prijs, afbeelding
 *   en variant-ID worden daarna door het theme zelf bijgewerkt; hier zit geen eigen variantlogica.
 * - Personalisaties worden verborgen inputs properties[...] met form="<productformulier>".
 * - De knop "In winkelwagen" blijft vergrendeld tot alle verplichte rijen zijn ingevuld.
 */
(() => {
  if (customElements.get('maatwerk-configurator')) return;

  const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  const LOCK_LABEL = 'Maak uw keuzes';
  const EMPTY_LABEL = 'Maak een keuze';
  const NUMBER_WORDS = { een: 1, één: 1, twee: 2, drie: 3, vier: 4, vijf: 5 };

  const ICONS = {
    chevron:
      '<svg viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2 4.25 6 8l4-3.75" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    check:
      '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="m3.5 8.5 3 3 6-7" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    heart:
      '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 20.5s-7.5-4.6-7.5-10.1A4.3 4.3 0 0 1 12 7.6a4.3 4.3 0 0 1 7.5 2.8c0 5.5-7.5 10.1-7.5 10.1Z"/></svg>',
    star:
      '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="m12 3.2 2.6 5.6 6.1.7-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6-4.5-4.2 6.1-.7Z"/></svg>',
    clover:
      '<svg viewBox="0 0 32 32" fill="currentColor" aria-hidden="true"><path d="M16 15.2c-1.6-3.9-6.9-4.9-7.6-1.3-.6 3.1 3.6 3.9 7.6 1.3Zm0 0c3.9-1.6 4.9-6.9 1.3-7.6-3.1-.6-3.9 3.6-1.3 7.6Zm0 0c1.6 3.9 6.9 4.9 7.6 1.3.6-3.1-3.6-3.9-7.6-1.3Zm0 0c-3.9 1.6-4.9 6.9-1.3 7.6 3.1.6 3.9-3.6 1.3-7.6Z" transform="rotate(45 16 16)"/><path d="M16.4 16.4c1.4 3 2.6 6.4 2.4 10.6" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>',
  };

  const KOORDMATEN = [
    ['Baby', '8 - 11 cm'],
    ['Kind', '11 - 13 cm'],
    ['XS', '13 - 16 cm'],
    ['S', '16 - 18 cm'],
    ['M', '18 - 20 cm'],
    ['L', '20 - 22 cm'],
  ];

  const LETTERTYPES = [
    { value: 'Klassiek (Arial)', title: 'Klassiek', sub: 'Arial', font: 'Arial, Helvetica, sans-serif', key: 'klassiek' },
    {
      value: 'Sierlijk (Lucida Handwriting)',
      title: 'Sierlijk',
      sub: 'Lucida Handwriting',
      font: "'Lucida Handwriting', 'Segoe Script', 'Apple Chancery', 'Snell Roundhand', cursive",
      key: 'sierlijk',
    },
  ];

  const MAX_COINS = 5;

  let uid = 0;

  const esc = (value) =>
    String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const formatMoney = (cents) => {
    const euros = cents / 100;
    const decimals = cents % 100 === 0 ? 0 : 2;
    return '€ ' + euros.toLocaleString('nl-NL', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const metalKey = (value) => {
    const v = value.toLowerCase();
    if (v.includes('geel')) return 'geel';
    if (v.includes('rood') || v.includes('rosé') || v.includes('rose')) return 'rood';
    if (v.includes('wit')) return 'wit';
    return null;
  };

  const variantType = (name, values) => {
    const n = name.toLowerCase();
    if (n.includes('munt')) return 'munten';
    if (n.includes('steen')) return 'steen';
    if (n.includes('ketting') || n.includes('collier')) return 'ketting';
    if (n.includes('maat')) return 'ringmaat';
    if (n.includes('goud') || n.includes('metaal') || values.every((v) => metalKey(v))) return 'goud';
    return 'pills';
  };

  const coinCount = (value) => {
    if (!value) return 0;
    const digits = value.match(/\d+/);
    if (digits) return Math.min(MAX_COINS, parseInt(digits[0], 10));
    const word = Object.keys(NUMBER_WORDS).find((w) => value.toLowerCase().includes(w));
    return word ? NUMBER_WORDS[word] : 0;
  };

  class MaatwerkConfigurator extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      try {
        this.config = JSON.parse(this.querySelector('[data-mw-config]').textContent);
        this.colors = JSON.parse(this.querySelector('[data-mw-colors]').textContent);
      } catch (error) {
        console.error('[maatwerk] Configuratie niet leesbaar', error);
        return;
      }

      this.formId = this.dataset.formId;
      this.section = this.closest('.shopify-section') || document;
      this.rowsEl = this.querySelector('[data-mw-rows]');
      this.summaryEl = this.querySelector('[data-mw-summary]');
      this.inputsEl = this.querySelector('[data-mw-inputs]');
      this.lockedHintId = this.querySelector('[id^="mw-locked-"]')?.id;
      this.rows = [];
      this.selected = [];
      this.props = {};

      this.#initVariantState();
      this.#buildRows();
      this.#bindCartGuards();
      this.#bindUrlSync();
      this.#refresh();
    }

    disconnectedCallback() {
      window.removeEventListener('click', this._onClickCapture, true);
      window.removeEventListener('submit', this._onSubmitCapture, true);
      this.section.removeEventListener('variant:update', this._onVariantUpdate);
      this._observer?.disconnect();
    }

    /**
     * Horizon zet ?variant= op het moment van de klik, met de data-variant-id van de vorige render. Bij snel
     * achter elkaar kiezen (de configurator wisselt soms meerdere opties) is die verouderd. Na de section-fetch
     * komt variant:update met de echte variant; daarmee de URL rechtzetten.
     */
    #bindUrlSync() {
      this._onVariantUpdate = (event) => {
        const id = event.detail?.resource?.id;
        if (!id || event.detail?.data?.newProduct) return;
        window.setTimeout(() => {
          const url = new URL(window.location.href);
          if (!url.pathname.includes('/products/') || url.searchParams.get('variant') === String(id)) return;
          url.searchParams.set('variant', String(id));
          history.replaceState(history.state, '', url.toString());
        }, 0);
      };
      this.section.addEventListener('variant:update', this._onVariantUpdate);
    }

    /* ---------- Varianten ---------- */

    #initVariantState() {
      const { options, variants, initialVariantId, initialOptions } = this.config;
      this.defaultVariant = variants.find((v) => v.available) || variants[0];
      this.variantCache = new Map();
      this.variantRequests = new Map();

      // product.variants (Liquid) stopt bij 250 varianten; de optiewaarden komen daarom uit options_with_values
      const valueLists = Array.isArray(this.config.optionValues) ? this.config.optionValues : [];
      this.optionValues = options.map((_, i) =>
        valueLists[i]?.length ? valueLists[i].map((v) => v.name) : [...new Set(variants.map((v) => v.options[i]))]
      );
      this.valueIds = options.map((_, i) => new Map((valueLists[i] || []).map((v) => [v.name, v.id])));

      const urlVariant = new URLSearchParams(window.location.search).has('variant');
      const fromUrl = urlVariant
        ? initialOptions || variants.find((v) => v.id === initialVariantId)?.options || null
        : null;

      this.selected = options.map((_, i) => {
        if (this.optionValues[i].length === 1) return this.optionValues[i][0];
        return fromUrl ? fromUrl[i] : null;
      });
    }

    #comboFor(optionIndex, value) {
      return this.config.options.map((_, j) =>
        j === optionIndex ? value : this.selected[j] ?? this.defaultVariant.options[j]
      );
    }

    /**
     * Variant voor een volledige combinatie. Eerst uit de (max. 250) varianten in de config, anders uit de
     * cache van #fetchVariant. Nog onbekend: undefined (de fetch start en ververst daarna de labels);
     * bestaat niet: null.
     */
    #lookup(combo) {
      const local = this.config.variants.find((v) => v.options.every((o, j) => o === combo[j]));
      if (local) return local;
      const key = JSON.stringify(combo);
      if (this.variantCache.has(key)) return this.variantCache.get(key);
      this.#fetchVariant(combo, key);
      return undefined;
    }

    /** Variant ophalen zoals Horizons variant-picker dat doet: section rendering met option_values. */
    #fetchVariant(combo, key) {
      if (this.variantRequests.has(key)) return this.variantRequests.get(key);
      const ids = combo.map((value, i) => this.valueIds[i].get(value));
      if (ids.some((id) => id == null) || !this.dataset.sectionId) {
        this.variantCache.set(key, null);
        return Promise.resolve(null);
      }

      const url = `${window.location.pathname}?section_id=${encodeURIComponent(this.dataset.sectionId)}&option_values=${ids.join(',')}`;
      const request = fetch(url)
        .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`HTTP ${response.status}`))))
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const json = html.querySelector('variant-picker script[type="application/json"]')?.textContent;
          const data = json ? JSON.parse(json) : null;
          // Bestaat de combinatie niet, dan geeft Shopify een andere variant terug
          const match = data && combo.every((o, j) => data.options?.[j] === o);
          const variant = match ? { id: data.id, options: data.options, price: data.price, available: data.available } : null;
          this.variantCache.set(key, variant);
          return variant;
        })
        .catch((error) => {
          console.warn('[maatwerk] Variant niet op te halen', combo, error);
          this.variantCache.set(key, null);
          return null;
        })
        .finally(() => {
          this.variantRequests.delete(key);
          this.#schedulePriceRefresh();
        });

      this.variantRequests.set(key, request);
      return request;
    }

    #schedulePriceRefresh() {
      if (this._priceRefresh) return;
      this._priceRefresh = window.requestAnimationFrame(() => {
        this._priceRefresh = null;
        this.#updatePriceLabels();
      });
    }

    #variantFor(optionIndex, value) {
      const exact = this.#lookup(this.#comboFor(optionIndex, value));
      if (exact !== null) return exact;
      return this.config.variants.find((v) => v.options[optionIndex] === value) || null;
    }

    /**
     * Prijs van de variant met deze opties; ontbrekende combinatie: goedkoopste bekende variant met die waarde.
     * null = nog niet bekend (wordt opgehaald).
     */
    #priceOf(combo, optionIndex) {
      const exact = this.#lookup(combo);
      if (exact) return exact.price;
      if (exact === undefined) return null;
      const prices = this.config.variants.filter((v) => v.options[optionIndex] === combo[optionIndex]).map((v) => v.price);
      return prices.length ? Math.min(...prices) : null;
    }

    /**
     * Basiswaarde per optie (vast, schuift niet mee met de keuze):
     * 1. "basiswaarden" in snippets/maatwerk-kleuren.liquid (bv. ketting: "1,0 mm");
     * 2. anders de waarde met de meest voorkomende prijs (bv. geboortesteen: alles behalve April);
     * 3. anders de eerste waarde.
     */
    #baseValues() {
      if (this._baseValues) return this._baseValues;
      const first = this.config.variants[0].options;
      const configured = this.colors.basiswaarden || {};
      let complete = true;
      const baseValues = this.config.options.map((name, i) => {
        const values = this.optionValues[i];
        const wanted = configured[variantType(name, values)];
        const byConfig = wanted && values.find((v) => v.toLowerCase().startsWith(String(wanted).toLowerCase()));
        if (byConfig) return byConfig;

        const prices = values.map((v) => this.#priceOf(first.map((o, j) => (j === i ? v : o)), i));
        if (prices.includes(null)) complete = false;
        const counts = new Map();
        prices.forEach((p) => p != null && counts.set(p, (counts.get(p) || 0) + 1));
        if (!counts.size) return values[0];
        const [modePrice, modeCount] = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0];
        return modeCount > 1 ? values[prices.indexOf(modePrice)] : values[0];
      });
      // Pas vastleggen als alle prijzen bekend zijn (opgehaalde varianten komen asynchroon binnen)
      if (complete) this._baseValues = baseValues;
      return baseValues;
    }

    /** Vaste prijzen per optiewaarde: de andere opties staan op hun basiswaarde. */
    #priceInfo(optionIndex) {
      const baseCombo = this.#baseValues();
      const prices = this.optionValues[optionIndex].map((value) =>
        this.#priceOf(baseCombo.map((o, j) => (j === optionIndex ? value : o)), optionIndex)
      );
      return { prices, base: this.#priceOf(baseCombo, optionIndex) };
    }

    #driveVariantPicker(optionIndex, value) {
      const picker = this.section.querySelector('variant-picker');
      if (!picker) return;

      const fieldset =
        picker.querySelector(`fieldset[data-fieldset-index="${optionIndex}"]`) ||
        picker.querySelectorAll('fieldset')[optionIndex];

      if (fieldset) {
        const input = [...fieldset.querySelectorAll('input[type="radio"]')].find((el) => el.value === value);
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      const select = picker.querySelectorAll('select')[optionIndex];
      if (select && select.value !== value) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /* ---------- Rijen opbouwen ---------- */

    #buildRows() {
      this.config.options.forEach((name, i) => {
        if (this.optionValues[i].length < 2) return;
        const type = variantType(name, this.optionValues[i]);
        this.#addRow({
          id: `opt${i}`,
          label: name,
          required: true,
          optionIndex: i,
          type,
          render: (body, row) => this.#renderVariant(body, row, i, type),
        });
      });

      const types = Array.isArray(this.config.personalisatie) ? this.config.personalisatie : [];
      types.forEach((rawType) => {
        const type = String(rawType).trim().toLowerCase();
        switch (type) {
          case 'initiaal':
            this.#addPropRow(type, 'Initiaal', (body, row) => this.#renderLetters(body, row, 'Initiaal'));
            break;
          case 'initiaal_of_symbool':
            this.#addPropRow(type, 'Initiaal of symbool', (body, row) => this.#renderInitialOrSymbol(body, row));
            break;
          case 'naam':
            this.#addPropRow(type, 'Naam', (body, row) => this.#renderName(body, row));
            break;
          case 'vulling':
            this.#addPropRow(type, 'Vullen met as of moedermelk', (body, row) => this.#renderFilling(body, row));
            break;
          case 'satijn_25':
          case 'satijn_41':
            this.#addPropRow(type, 'Satijnkleur', (body, row) => this.#renderSatin(body, row, type));
            break;
          case 'koordmaat':
            this.#addPropRow(type, 'Koordmaat', (body, row) => this.#renderCord(body, row));
            break;
          case 'munten_gravure':
            for (let n = 1; n <= MAX_COINS; n++) {
              this.#addPropRow(`munt${n}`, `Munt ${n}`, (body, row) => this.#renderCoin(body, row, n), { coin: n });
            }
            break;
          case 'lettertype':
            this.#addPropRow(type, 'Lettertype', (body, row) => this.#renderFont(body, row));
            break;
          default:
            if (type) console.warn(`[maatwerk] Onbekend personalisatietype: ${type}`);
        }
      });

      this.#addPropRow('bijzonderheden', 'Bijzonderheden', (body, row) => this.#renderNotes(body, row), {
        required: false,
        optionalLabel: true,
      });
    }

    #addPropRow(type, label, render, extra = {}) {
      this.#addRow({ id: type, label, type, required: true, render, ...extra });
    }

    #addRow(row) {
      const n = ++uid;
      const headId = `mw-head-${n}`;
      const panelId = `mw-panel-${n}`;
      const el = document.createElement('div');
      el.className = 'mw-row';
      el.dataset.row = row.id;
      el.innerHTML = `
        <button type="button" class="mw-row__head" id="${headId}" aria-expanded="false" aria-controls="${panelId}">
          <span class="mw-row__label">${esc(row.label)}${row.optionalLabel ? ' <small>(optioneel)</small>' : ''}</span>
          <span class="mw-row__value is-empty">${row.optionalLabel ? 'Optioneel' : EMPTY_LABEL}</span>
          <span class="mw-row__check">${ICONS.check}</span>
          <span class="mw-row__chev">${ICONS.chevron}</span>
        </button>
        <div class="mw-row__panel" id="${panelId}" role="region" aria-labelledby="${headId}" inert>
          <div class="mw-row__inner"><div class="mw-row__body"></div></div>
        </div>
        <p class="mw-row__note" hidden></p>`;

      row.el = el;
      row.head = el.querySelector('.mw-row__head');
      row.valueEl = el.querySelector('.mw-row__value');
      row.panel = el.querySelector('.mw-row__panel');
      row.body = el.querySelector('.mw-row__body');
      row.note = el.querySelector('.mw-row__note');
      row.value = null;
      row.summary = null;

      row.head.addEventListener('click', () => this.#toggle(row));
      row.render(row.body, row);

      this.rowsEl.appendChild(el);
      this.rows.push(row);
    }

    /* ---------- Open/dicht ---------- */

    #toggle(row, force) {
      const open = force ?? !row.el.classList.contains('is-open');
      this.rows.forEach((r) => {
        if (r !== row) this.#setOpen(r, false);
      });
      this.#setOpen(row, open);
    }

    #setOpen(row, open) {
      row.el.classList.toggle('is-open', open);
      row.head.setAttribute('aria-expanded', String(open));
      row.panel.inert = !open;
      if (open) row.el.classList.remove('is-missing');
    }

    #visibleRows() {
      return this.rows.filter((r) => !r.el.hidden);
    }

    #isComplete(row) {
      return !row.required || row.value != null;
    }

    #missingRows() {
      return this.#visibleRows().filter((r) => !this.#isComplete(r));
    }

    /** Na een keuze: rij dicht, volgende onvoltooide rij open. */
    #advance(row, event) {
      const visible = this.#visibleRows();
      const start = visible.indexOf(row);
      const next = [...visible.slice(start + 1), ...visible.slice(0, start)].find(
        (r) => r.required && !this.#isComplete(r)
      );

      this.#setOpen(row, false);
      if (!next) return;

      this.#toggle(next, true);
      const viaKeyboard = event && event.detail === 0;
      window.setTimeout(() => {
        const rect = next.el.getBoundingClientRect();
        if (rect.top < 80 || rect.top > window.innerHeight * 0.7) {
          next.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        if (viaKeyboard) next.head.focus({ preventScroll: true });
      }, 260);
    }

    /* ---------- Waarde zetten ---------- */

    #setValue(row, value, display, summary) {
      row.value = value;
      row.summary = value == null ? null : summary ?? display;
      row.valueEl.textContent = value == null ? (row.optionalLabel ? 'Optioneel' : EMPTY_LABEL) : display;
      row.valueEl.classList.toggle('is-empty', value == null);
      row.el.classList.toggle('is-complete', value != null && row.required);
      row.el.classList.remove('is-missing');
    }

    #setProperty(name, value) {
      let input = this.inputsEl.querySelector(`input[data-prop="${CSS.escape(name)}"]`);
      if (value == null || value === '') {
        input?.remove();
        delete this.props[name];
        return;
      }
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${name}]`;
        input.dataset.prop = name;
        input.setAttribute('form', this.formId);
        this.inputsEl.appendChild(input);
      }
      input.value = value;
      this.props[name] = value;
    }

    #press(group, button) {
      group.querySelectorAll('[aria-pressed]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
    }

    /* ---------- Kiezers: varianten ---------- */

    #renderVariant(body, row, optionIndex, type) {
      const values = this.optionValues[optionIndex];
      const group = document.createElement('div');
      group.setAttribute('role', 'group');
      group.setAttribute('aria-label', this.config.options[optionIndex]);

      if (type === 'ringmaat' && this.config.sizeGuide) {
        const hint = document.createElement('p');
        hint.className = 'mw-hint';
        hint.innerHTML = `Twijfelt u over de maat? <a href="${esc(this.config.sizeGuide)}" target="_blank" rel="noopener">Bekijk de maatgids</a>`;
        body.appendChild(hint);
      }

      if (type === 'goud') group.className = 'mw-swatches';
      else if (type === 'steen') group.className = 'mw-gems';
      else group.className = 'mw-pills';

      values.forEach((value) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.dataset.value = value;
        btn.setAttribute('aria-pressed', 'false');

        if (type === 'goud') {
          const m = this.colors.metaal?.[metalKey(value)] || ['#eee', '#ddd', '#ccc'];
          btn.className = 'mw-opt mw-swatch';
          btn.innerHTML = `<span class="mw-swatch__dot mw-metal" style="--mw-m1:${m[0]};--mw-m2:${m[1]};--mw-m3:${m[2]}"></span>
            <span class="mw-swatch__name">${esc(value)}</span><span class="mw-price" data-price></span>`;
        } else if (type === 'steen') {
          const gem = this.#gemFor(value);
          btn.className = 'mw-opt mw-swatch';
          btn.innerHTML = `<span class="mw-swatch__dot mw-gem" style="--mw-fill:${gem?.kleur || '#e5e5e5'}"></span>
            <span class="mw-swatch__sub">${esc(gem?.maand || '')}</span>
            <span class="mw-swatch__name">${esc(gem?.naam || value)}</span><span class="mw-price" data-price></span>`;
        } else if (type === 'ketting') {
          const [main, ...rest] = value.split('/');
          btn.className = 'mw-opt mw-pill';
          btn.innerHTML = `<span class="mw-pill__main">${esc(main.trim())}</span>
            ${rest.length ? `<span class="mw-pill__sub">${esc(rest.join('/').trim())}</span>` : ''}
            <span class="mw-price" data-price></span>`;
        } else {
          btn.className = 'mw-opt mw-pill';
          btn.innerHTML = `<span class="mw-pill__main">${esc(value)}</span><span class="mw-price" data-price></span>`;
        }

        btn.addEventListener('click', (event) => {
          this.selected[optionIndex] = value;
          this.#press(group, btn);
          this.#setValue(row, value, this.#variantDisplay(type, value), this.#variantSummary(type, value));
          this.#driveVariantPicker(optionIndex, value);
          this.#refresh();
          this.#advance(row, event);
        });

        group.appendChild(btn);

        // Variant uit de URL (?variant=) geldt als gemaakte keuze
        if (this.selected[optionIndex] === value) {
          btn.setAttribute('aria-pressed', 'true');
          this.#setValue(row, value, this.#variantDisplay(type, value), this.#variantSummary(type, value));
        }
      });

      body.appendChild(group);
    }

    #gemFor(value) {
      const v = value.toLowerCase();
      const list = this.colors.geboortesteen || [];
      return (
        list.find((g) => v.includes(g.steen)) ||
        list.find((g) => v.includes(g.maand.toLowerCase())) ||
        null
      );
    }

    #variantDisplay(type, value) {
      if (type === 'steen') {
        const gem = this.#gemFor(value);
        return gem ? `${gem.naam} (${gem.maand.toLowerCase()})` : value;
      }
      if (type === 'ketting') return value.replace(/\s*\/\s*/, ' · ');
      return value;
    }

    #variantSummary(type, value) {
      if (type === 'steen') return this.#gemFor(value)?.naam || value;
      if (type === 'ketting') return value.split('/')[0].trim();
      if (type === 'ringmaat') return `Ringmaat ${value}`;
      if (type === 'munten') {
        const n = coinCount(value);
        return n ? `${n} ${n === 1 ? 'munt' : 'munten'}` : value;
      }
      return value;
    }

    #updatePriceLabels() {
      this.rows
        .filter((r) => r.optionIndex != null)
        .forEach((row) => {
          // Vast verschil t.o.v. de basiswaarde (bv. 0,8 mm "− € 70"); aantal munten toont de totaalprijs
          const { prices, base } = this.#priceInfo(row.optionIndex);
          const buttons = row.body.querySelectorAll('[data-value]');
          buttons.forEach((btn, i) => {
            const label = btn.querySelector('[data-price]');
            const known = prices[i] != null && base != null;
            const diff = known ? prices[i] - base : 0;
            if (label) {
              if (row.type === 'munten') label.textContent = prices[i] != null ? formatMoney(prices[i]) : '';
              else if (diff > 0) label.textContent = `+ ${formatMoney(diff)}`;
              else if (diff < 0) label.textContent = `− ${formatMoney(-diff)}`;
              else label.textContent = '';
            }
            // undefined = wordt nog opgehaald: niet als onbeschikbaar tonen
            const variant = this.#variantFor(row.optionIndex, btn.dataset.value);
            btn.classList.toggle('is-unavailable', variant !== undefined && (!variant || !variant.available));
          });
        });
    }

    /* ---------- Kiezers: personalisatie ---------- */

    #letterButtons(grid, onPick, extra = []) {
      [...extra, ...LETTERS.map((l) => ({ value: l, html: l, display: l }))].forEach((item) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `mw-opt mw-tile${item.wide ? ' mw-tile--wide' : ''}`;
        btn.dataset.value = item.value;
        btn.setAttribute('aria-pressed', 'false');
        if (item.label) btn.setAttribute('aria-label', item.label);
        btn.innerHTML = item.html;
        btn.addEventListener('click', (event) => {
          this.#press(grid, btn);
          onPick(item, event);
        });
        grid.appendChild(btn);
      });
    }

    #renderLetters(body, row, propName) {
      const grid = document.createElement('div');
      grid.className = 'mw-letters';
      grid.setAttribute('role', 'group');
      grid.setAttribute('aria-label', 'Kies een letter');
      this.#letterButtons(grid, (item, event) => {
        this.#setProperty(propName, item.value);
        this.#setValue(row, item.value, item.value, `Initiaal ${item.value}`);
        this.#refresh();
        this.#advance(row, event);
      });
      body.appendChild(grid);
    }

    #renderInitialOrSymbol(body, row) {
      const segment = document.createElement('div');
      segment.className = 'mw-segment';
      segment.setAttribute('role', 'group');
      segment.setAttribute('aria-label', 'Initiaal of symbool');
      segment.innerHTML = `
        <button type="button" class="mw-segment__btn" data-mode="initiaal" aria-pressed="true">Initiaal</button>
        <button type="button" class="mw-segment__btn" data-mode="symbool" aria-pressed="false">Symbool</button>`;

      const letters = document.createElement('div');
      letters.className = 'mw-letters';
      letters.setAttribute('role', 'group');
      letters.setAttribute('aria-label', 'Kies een letter');

      const symbols = document.createElement('div');
      symbols.className = 'mw-pills';
      symbols.setAttribute('role', 'group');
      symbols.setAttribute('aria-label', 'Kies een symbool');
      symbols.hidden = true;
      symbols.innerHTML = `<button type="button" class="mw-opt mw-tile mw-tile--symbol" data-value="Klaver" aria-pressed="false">${ICONS.clover}<span>Klaver</span></button>`;

      const showMode = (mode) => {
        segment.querySelectorAll('[data-mode]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.mode === mode)));
        letters.hidden = mode !== 'initiaal';
        symbols.hidden = mode !== 'symbool';
      };

      segment.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-mode]');
        if (btn) showMode(btn.dataset.mode);
      });

      this.#letterButtons(letters, (item, event) => {
        this.#press(symbols, null);
        this.#setProperty('Symbool', null);
        this.#setProperty('Initiaal', item.value);
        this.#setValue(row, item.value, `Initiaal ${item.value}`);
        this.#refresh();
        this.#advance(row, event);
      });

      const clover = symbols.querySelector('button');
      clover.addEventListener('click', (event) => {
        this.#press(letters, null);
        this.#press(symbols, clover);
        this.#setProperty('Initiaal', null);
        this.#setProperty('Symbool', 'Klaver');
        this.#setValue(row, 'Klaver', 'Symbool Klaver');
        this.#refresh();
        this.#advance(row, event);
      });

      body.append(segment, letters, symbols);
    }

    #renderName(body, row) {
      const id = `mw-name-${++uid}`;
      body.innerHTML = `
        <label class="visually-hidden" for="${id}">Naam (maximaal 10 tekens)</label>
        <div class="mw-field">
          <input class="mw-input" id="${id}" type="text" maxlength="10" autocomplete="off" spellcheck="false" placeholder="Bijvoorbeeld: Sophie">
          <button type="button" class="mw-confirm" disabled>Bevestigen</button>
        </div>
        <p class="mw-counter" aria-live="polite"><span data-count>0</span>/10</p>
        <div class="mw-preview" aria-hidden="true"><span class="mw-preview__text is-placeholder">Sophie</span></div>`;

      const input = body.querySelector('input');
      const confirm = body.querySelector('.mw-confirm');
      const count = body.querySelector('[data-count]');
      const preview = body.querySelector('.mw-preview__text');
      this.namePreview = body.querySelector('.mw-preview');

      const update = () => {
        const value = input.value.slice(0, 10);
        const trimmed = value.trim();
        count.textContent = String(value.length);
        preview.textContent = trimmed || 'Sophie';
        preview.classList.toggle('is-placeholder', !trimmed);
        confirm.disabled = !trimmed;
        this.#setProperty('Naam', trimmed || null);
        this.#setValue(row, trimmed || null, trimmed, `Naam ${trimmed}`);
        this.#refresh();
      };

      input.addEventListener('input', update);
      input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        if (input.value.trim()) this.#advance(row, { detail: 0 });
      });
      confirm.addEventListener('click', (event) => this.#advance(row, event));
    }

    #renderFilling(body, row) {
      const group = document.createElement('div');
      group.className = 'mw-cards';
      group.setAttribute('role', 'group');
      group.setAttribute('aria-label', 'Vullen met as of moedermelk');
      group.innerHTML = `
        <button type="button" class="mw-opt mw-card" data-value="Ja" aria-pressed="false"><span class="mw-card__title">Ja</span><span class="mw-card__sub">Met vulling</span></button>
        <button type="button" class="mw-opt mw-card" data-value="Nee" aria-pressed="false"><span class="mw-card__title">Nee</span><span class="mw-card__sub">Zonder vulling</span></button>`;

      group.querySelectorAll('button').forEach((btn) => {
        btn.addEventListener('click', (event) => {
          const yes = btn.dataset.value === 'Ja';
          this.#press(group, btn);
          this.#setProperty('Vullen met as of moedermelk', btn.dataset.value);
          this.#setValue(row, btn.dataset.value, btn.dataset.value, yes ? 'Met vulling' : 'Zonder vulling');
          row.note.textContent = 'Wij nemen na uw bestelling contact met u op over het aanleveren.';
          row.note.hidden = !yes;
          this.#refresh();
          this.#advance(row, event);
        });
      });
      body.appendChild(group);
    }

    #renderSatin(body, row, type) {
      const list = this.colors[type] || [];
      const grid = document.createElement('div');
      grid.className = 'mw-satin';
      grid.setAttribute('role', 'group');
      grid.setAttribute('aria-label', 'Satijnkleur');
      const caption = document.createElement('p');
      caption.className = 'mw-satin__caption';
      caption.textContent = 'Beweeg over of tik op een kleur';

      const describe = (c) => `<strong>${c.nr}</strong> ${esc(c.naam)}`;
      let chosen = null;

      list.forEach((c) => {
        const label = `${c.nr} ${c.naam}`;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `mw-opt${c.lurex ? ' is-lurex' : ''}`;
        btn.style.setProperty('--mw-fill', c.kleur);
        btn.title = label;
        btn.setAttribute('aria-label', label);
        btn.setAttribute('aria-pressed', 'false');
        btn.addEventListener('mouseenter', () => (caption.innerHTML = describe(c)));
        btn.addEventListener('focus', () => (caption.innerHTML = describe(c)));
        btn.addEventListener('click', (event) => {
          chosen = c;
          this.#press(grid, btn);
          caption.innerHTML = `Gekozen: ${describe(c)}`;
          this.#setProperty('Satijnkleur', label);
          this.#setValue(row, label, label, `Satijn ${label}`);
          this.#refresh();
          this.#advance(row, event);
        });
        grid.appendChild(btn);
      });

      grid.addEventListener('mouseleave', () => {
        caption.innerHTML = chosen ? `Gekozen: ${describe(chosen)}` : 'Beweeg over of tik op een kleur';
      });

      body.append(grid, caption);
    }

    #renderCord(body, row) {
      const group = document.createElement('div');
      group.className = 'mw-pills';
      group.setAttribute('role', 'group');
      group.setAttribute('aria-label', 'Koordmaat');
      KOORDMATEN.forEach(([size, range]) => {
        const value = `${size} (${range})`;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'mw-opt mw-pill';
        btn.setAttribute('aria-pressed', 'false');
        btn.innerHTML = `<span class="mw-pill__main">${esc(size)}</span><span class="mw-pill__sub">${esc(range)}</span>`;
        btn.addEventListener('click', (event) => {
          this.#press(group, btn);
          this.#setProperty('Koordmaat', value);
          this.#setValue(row, value, value, `Koord ${size}`);
          this.#refresh();
          this.#advance(row, event);
        });
        group.appendChild(btn);
      });
      body.appendChild(group);
    }

    #renderCoin(body, row, n) {
      const grid = document.createElement('div');
      grid.className = 'mw-letters';
      grid.setAttribute('role', 'group');
      grid.setAttribute('aria-label', `Gravering munt ${n}`);
      const extra = [
        { value: 'Geen gravering', html: 'Geen gravering', display: 'Geen gravering', wide: true },
        { value: 'Hartje', html: ICONS.heart, display: 'Hartje', label: 'Hartje' },
        { value: 'Sterretje', html: ICONS.star, display: 'Sterretje', label: 'Sterretje' },
      ];
      this.#letterButtons(
        grid,
        (item, event) => {
          this.#setProperty(`Munt ${n}`, item.value);
          this.#setValue(row, item.value, item.display, `Munt ${n}: ${item.display}`);
          this.#refresh();
          this.#advance(row, event);
        },
        extra
      );
      body.appendChild(grid);
    }

    #renderFont(body, row) {
      const group = document.createElement('div');
      group.className = 'mw-cards';
      group.setAttribute('role', 'group');
      group.setAttribute('aria-label', 'Lettertype');
      LETTERTYPES.forEach((f) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'mw-opt mw-card';
        btn.setAttribute('aria-pressed', 'false');
        btn.innerHTML = `<span class="mw-card__sample" style="font-family:${f.font}" aria-hidden="true">Aa</span>
          <span class="mw-card__title">${esc(f.title)}</span><span class="mw-card__sub">${esc(f.sub)}</span>`;
        btn.addEventListener('click', (event) => {
          this.#press(group, btn);
          this.#setProperty('Lettertype', f.value);
          this.#setValue(row, f.value, f.value, f.title);
          this.namePreview?.classList.toggle('mw-preview--klassiek', f.key === 'klassiek');
          this.#refresh();
          this.#advance(row, event);
        });
        group.appendChild(btn);
      });
      body.appendChild(group);
    }

    #renderNotes(body, row) {
      const id = `mw-notes-${++uid}`;
      body.innerHTML = `
        <label class="visually-hidden" for="${id}">Bijzonderheden</label>
        <textarea class="mw-textarea" id="${id}" rows="3" maxlength="500" placeholder="Heeft u speciale wensen? Laat het ons weten."></textarea>`;
      const textarea = body.querySelector('textarea');
      textarea.addEventListener('input', () => {
        const value = textarea.value.trim();
        this.#setProperty('Bijzonderheden', value || null);
        this.#setValue(row, value || null, value);
      });
    }

    /* ---------- Status, samenvatting, munten ---------- */

    #refresh() {
      this.#syncCoinRows();
      this.#updatePriceLabels();
      this.#updateSummary();
      this.#updateLock();
    }

    #syncCoinRows() {
      const coinRows = this.rows.filter((r) => r.coin);
      if (!coinRows.length) return;

      const coinOption = this.config.options.findIndex((name, i) => variantType(name, this.optionValues[i]) === 'munten');
      const count = coinOption === -1 ? 1 : coinCount(this.selected[coinOption]);

      coinRows.forEach((row) => {
        const show = row.coin <= count;
        if (row.el.hidden === !show) return;
        row.el.hidden = !show;
        if (!show) {
          this.#setOpen(row, false);
        }
      });

      // Properties alleen meesturen voor zichtbare munten
      coinRows.forEach((row) => {
        const name = `Munt ${row.coin}`;
        this.#setProperty(name, row.el.hidden ? null : row.value);
      });
    }

    #updateSummary() {
      const parts = this.#visibleRows()
        .filter((r) => r.summary && r.id !== 'bijzonderheden')
        .map((r) => esc(r.summary));
      this.summaryEl.hidden = parts.length === 0;
      this.summaryEl.innerHTML = parts.length ? `<strong>Uw keuze:</strong> ${parts.join(' · ')}` : '';
    }

    /* ---------- Winkelwagenknop ---------- */

    get complete() {
      return this.#missingRows().length === 0;
    }

    #atcContainer() {
      const form = document.getElementById(this.formId);
      return form?.querySelector('add-to-cart-component') || null;
    }

    /** Alle knoppen die "In winkelwagen" doen: de echte knop en de sticky balken. */
    #lockTargets() {
      const targets = [];
      const main = this.#atcContainer();
      if (main) {
        targets.push({
          container: main,
          button: () => main.querySelector('[ref="addToCartButton"], .add-to-cart-button'),
          text: () => main.querySelector('.add-to-cart-text__content span span') || main.querySelector('.add-to-cart-text__content'),
        });
      }
      document.querySelectorAll('sticky-add-to-cart').forEach((bar) => {
        targets.push({
          container: bar,
          button: () => bar.querySelector('[ref="addToCartButton"]'),
          text: () => bar.querySelector('[ref="addToCartButton"] .add-to-cart-text__content > span:first-child'),
        });
      });
      document.querySelectorAll('[data-pdp-atc-bar]').forEach((bar) => {
        targets.push({
          container: bar,
          button: () => bar.querySelector('[data-pdp-atc-trigger]'),
          text: () => bar.querySelector('[data-pdp-atc-trigger]'),
        });
      });
      return targets;
    }

    #updateLock() {
      const locked = !this.complete;
      if (!this._lockTargets) {
        this._lockTargets = this.#lockTargets();
        // Het theme vervangt knopinhoud na een variantwissel; daarna de vergrendeling opnieuw toepassen.
        this._observer = new MutationObserver((records) => {
          const touched = new Set(records.map((r) => this._lockTargets.find((t) => t.container.contains(r.target))));
          touched.forEach((t) => t && this.#applyLock(t));
        });
        this._lockTargets.forEach((t) =>
          this._observer.observe(t.container, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'disabled'] })
        );
      }
      this._locked = locked;
      this._lockTargets.forEach((t) => {
        t.container.toggleAttribute('data-mw-locked', locked);
        this.#applyLock(t);
      });
    }

    #applyLock(target) {
      const button = target.button();
      const textEl = target.text();
      if (!button) return;

      if (this._locked) {
        if (!button.classList.contains('mw-is-locked')) button.classList.add('mw-is-locked');
        if (button.getAttribute('aria-disabled') !== 'true') button.setAttribute('aria-disabled', 'true');
        if (this.lockedHintId && button.getAttribute('aria-describedby') !== this.lockedHintId) {
          button.setAttribute('aria-describedby', this.lockedHintId);
        }
        if (textEl && textEl.textContent.trim() !== LOCK_LABEL) {
          target.original = textEl.innerHTML;
          textEl.textContent = LOCK_LABEL;
        }
      } else {
        if (button.classList.contains('mw-is-locked')) button.classList.remove('mw-is-locked');
        if (button.hasAttribute('aria-disabled')) button.removeAttribute('aria-disabled');
        if (button.hasAttribute('aria-describedby')) button.removeAttribute('aria-describedby');
        if (textEl && textEl.textContent.trim() === LOCK_LABEL && target.original) {
          textEl.innerHTML = target.original;
        }
      }
    }

    #revealMissing() {
      const missing = this.#missingRows();
      missing.forEach((r) => r.el.classList.add('is-missing'));
      const first = missing[0];
      if (!first) return;
      this.#toggle(first, true);
      first.el.classList.add('is-missing');
      first.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => first.head.focus({ preventScroll: true }), 400);
    }

    #bindCartGuards() {
      const isGuarded = (target) => {
        if (!(target instanceof Element)) return false;
        const mainButton = target.closest('[ref="addToCartButton"], .add-to-cart-button');
        // Let op: form.id is overschaduwd door <input name="id">, daarom getAttribute
        if (mainButton && mainButton.closest('form')?.getAttribute('id') === this.formId) return true;
        return Boolean(target.closest('sticky-add-to-cart [ref="addToCartButton"], [data-pdp-atc-trigger]'));
      };

      this._onClickCapture = (event) => {
        if (this.complete || !isGuarded(event.target)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const mainButton = this.#atcContainer()?.querySelector('[ref="addToCartButton"]');
        if (mainButton) mainButton.dataset.puppet = 'false';
        this.#revealMissing();
      };

      this._onSubmitCapture = (event) => {
        if (this.complete || !(event.target instanceof HTMLFormElement) || event.target.getAttribute('id') !== this.formId) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.#revealMissing();
      };

      window.addEventListener('click', this._onClickCapture, true);
      window.addEventListener('submit', this._onSubmitCapture, true);
    }
  }

  customElements.define('maatwerk-configurator', MaatwerkConfigurator);
})();
